import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Loader,
  RefreshCw,
  AlertTriangle,
  Star,
  Database,
  Sparkles,
  Clock,
  Info,
} from "lucide-react";
import {
  streamSnapshot,
  getWatchlistItems,
  addToWatchlist,
  removeFromWatchlist,
  isInWatchlist,
} from "../../services/researchService";
import { getCurrentUid } from "../../services/disclosureService";
import MarkdownView from "./MarkdownView";

/**
 * StockResearchModal — opens a full-screen modal for a given ticker
 * and streams the arc-research-proxy snapshot into view. Consumes the
 * SSE event stream from researchService.streamSnapshot and renders
 * progressively: EDGAR fundamentals appear first (~1s), then the AI
 * narrative streams in chunk-by-chunk (~60-90s fresh, instant on
 * cache hits).
 *
 * Props:
 *   ticker       — Ticker symbol to load. Triggers a new stream when
 *                  this changes. null/undefined = modal closed state.
 *   onClose      — Required. Called when user closes the modal.
 *   forceRefresh — If true on open, bypasses Firestore cache and
 *                  forces a fresh AI generation. Used by the Refresh
 *                  button inside the modal.
 *
 * SSE event handling:
 *   onSnapshot   — sets fundamentals (renders the header + table)
 *   onDataBlock  — captured for debug, not shown by default
 *   onAIStart    — flips loading-AI flag, sets model name
 *   onChunk      — appends text to aiText (live streaming)
 *   onAIDone     — stamps final metadata (stopReason, tokens, usage)
 *   onCached     — marks the snapshot as a cache hit + shows timestamp
 *   onComplete   — flips loading flag off
 *   onError      — surfaces error state with typed code rendering
 *
 * Cleanup: an AbortController cancels the stream on unmount or when
 * the ticker changes, so we don't leak fetches when the user closes
 * the modal mid-generation.
 */

const StockResearchModal = ({ ticker, onClose, forceRefresh = false }) => {
  /* ─── Stream state ──────────────────────────────────────────── */
  const [snapshot, setSnapshot] = useState(null);
  const [aiText, setAiText] = useState("");
  const [aiMeta, setAiMeta] = useState(null);
  const [cached, setCached] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState("connecting"); // connecting | fundamentals | ai-streaming | done
  const [error, setError] = useState(null);
  // Partial-data state: set when the Worker emits `no_ai` (ETFs,
  // funds, recent IPOs, foreign filers). We have metadata but no
  // income statement, so no AI was generated. Render a different
  // empty state explaining why + suggesting alternatives.
  const [noAi, setNoAi] = useState(null); // { reason, entityType }

  /* ─── Watchlist state ───────────────────────────────────────── */
  const [watchlistItems, setWatchlistItems] = useState([]);
  const [savingWatchlist, setSavingWatchlist] = useState(false);

  const abortRef = useRef(null);

  /* ─── Stream effect ─────────────────────────────────────────── */
  useEffect(() => {
    if (!ticker) return;

    // Reset state for the new ticker (covers the case where the
    // modal is reused for a different ticker without unmounting).
    setSnapshot(null);
    setAiText("");
    setAiMeta(null);
    setCached(null);
    setError(null);
    setNoAi(null);
    setLoading(true);
    setPhase("connecting");

    const controller = new AbortController();
    abortRef.current = controller;

    streamSnapshot(
      { ticker, forceRefresh, signal: controller.signal },
      {
        onSnapshot: (s) => {
          setSnapshot(s);
          setPhase("fundamentals");
        },
        onAIStart: () => setPhase("ai-streaming"),
        onChunk: ({ text }) => setAiText((prev) => prev + text),
        onAIDone: (payload) => setAiMeta(payload),
        onCached: (payload) => setCached(payload),
        onNoAI: (payload) => {
          // Worker shipped us a metadata-only snapshot (ETF, fund,
          // foreign filer, etc.) with no AI. Stash the explanation
          // so the body renders the partial-data view instead of
          // waiting forever for chunks.
          setNoAi(payload);
          setPhase("done");
          setLoading(false);
        },
        onComplete: () => {
          setLoading(false);
          setPhase("done");
        },
        onError: (err) => {
          setError(err);
          setLoading(false);
          setPhase("done");
        },
      }
    ).catch((err) => {
      // Hard rejection (missing config, bad URL) — already dispatched
      // to onError, but log for debugging.
      // eslint-disable-next-line no-console
      console.warn("[StockResearchModal] stream rejected:", err.message);
    });

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticker, forceRefresh]);

  /* ─── Watchlist load on mount ───────────────────────────────── */
  useEffect(() => {
    const uid = getCurrentUid();
    if (!uid) return;
    let cancelled = false;
    getWatchlistItems(uid).then((items) => {
      if (!cancelled) setWatchlistItems(items);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  /* ─── Escape key handler ────────────────────────────────────── */
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape" && onClose) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  /* ─── Helpers ───────────────────────────────────────────────── */

  const isSaved = isInWatchlist(watchlistItems, ticker);

  const handleToggleWatchlist = async () => {
    const uid = getCurrentUid();
    if (!uid) return;
    setSavingWatchlist(true);
    try {
      const updated = isSaved
        ? await removeFromWatchlist(uid, ticker)
        : await addToWatchlist(uid, ticker);
      setWatchlistItems(updated);
      // Broadcast so the WatchlistGrid (and any other listener) can
      // re-fetch its state without us having to thread a prop through.
      // Cross-component pub/sub via window events keeps this modal
      // independent of where in the app the watchlist surface lives.
      window.dispatchEvent(new CustomEvent("watchlistUpdated"));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[StockResearchModal] watchlist toggle failed:", err);
    } finally {
      setSavingWatchlist(false);
    }
  };

  const handleRefresh = () => {
    if (!ticker || loading) return;
    // Re-trigger the stream with forceRefresh — caller passes
    // forceRefresh via prop but for an in-place refresh we just
    // bump a local key. Simplest: re-mount the effect by changing
    // ticker key (we'd need a key prop), but here we abort + reload.
    abortRef.current?.abort();
    setSnapshot(null);
    setAiText("");
    setAiMeta(null);
    setCached(null);
    setError(null);
    setNoAi(null);
    setLoading(true);
    setPhase("connecting");

    const controller = new AbortController();
    abortRef.current = controller;
    streamSnapshot(
      { ticker, forceRefresh: true, signal: controller.signal },
      {
        onSnapshot: (s) => {
          setSnapshot(s);
          setPhase("fundamentals");
        },
        onAIStart: () => setPhase("ai-streaming"),
        onChunk: ({ text }) => setAiText((prev) => prev + text),
        onAIDone: (payload) => setAiMeta(payload),
        onCached: (payload) => setCached(payload),
        onNoAI: (payload) => {
          setNoAi(payload);
          setPhase("done");
          setLoading(false);
        },
        onComplete: () => {
          setLoading(false);
          setPhase("done");
        },
        onError: (err) => {
          setError(err);
          setLoading(false);
          setPhase("done");
        },
      }
    ).catch(() => {});
  };

  /* ─── Render ────────────────────────────────────────────────── */

  if (!ticker) return null;

  const modal = (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/80 backdrop-blur-sm overflow-y-auto py-6 px-3"
      onClick={onClose}
    >
      <div
        className="surface-xl w-full max-w-3xl my-auto relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close research modal"
          className="absolute top-3 right-3 p-1.5 rounded-lg text-arc-dim hover:text-arc-ink hover:bg-arc-surface transition-colors"
        >
          <X size={18} />
        </button>

        {/* Header */}
        <div className="p-6 border-b border-arc-line">
          <div className="flex items-baseline gap-2 mb-1">
            <h2 className="text-2xl font-bold text-arc-ink tracking-tight">
              {ticker}
            </h2>
            {snapshot?.name && (
              <span className="text-arc-muted text-sm truncate">
                · {snapshot.name}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono uppercase tracking-[0.14em] text-arc-dim">
            {snapshot?.sector && <Chip>{snapshot.sector}</Chip>}
            {snapshot?.exchange && <Chip>{snapshot.exchange}</Chip>}
            {snapshot?.resolvedFrom && (
              <Chip muted>
                <Database size={10} className="inline -mt-px mr-1" />
                {snapshot.resolvedFrom}
              </Chip>
            )}
            {snapshot?.fundamentals?.asOfDate && (
              <Chip muted>data as of {snapshot.fundamentals.asOfDate}</Chip>
            )}
            {cached && (
              <Chip gold>
                <Clock size={10} className="inline -mt-px mr-1" />
                cached {timeAgo(cached.cachedAt)}
              </Chip>
            )}
            {!cached && aiMeta?.generatedAt && (
              <Chip gold>
                <Sparkles size={10} className="inline -mt-px mr-1" />
                fresh
              </Chip>
            )}
          </div>
        </div>

        {/* Action bar */}
        <div className="px-6 py-3 border-b border-arc-line flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={handleToggleWatchlist}
            disabled={savingWatchlist || !getCurrentUid()}
            className={`btn-tactile inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all disabled:opacity-50 ${
              isSaved
                ? "bg-arc-gold text-arc-bg border-arc-gold"
                : "bg-transparent text-arc-ink border-arc-line hover:border-arc-gold/40"
            }`}
          >
            <Star
              size={12}
              className={isSaved ? "fill-current" : ""}
            />
            {savingWatchlist
              ? "Saving…"
              : isSaved
              ? "Saved to watchlist"
              : "Save to watchlist"}
          </button>

          <button
            type="button"
            onClick={handleRefresh}
            disabled={loading}
            className="btn-tactile inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-arc-line text-arc-muted hover:text-arc-ink hover:border-arc-gold/40 disabled:opacity-50 transition-all"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
            {loading ? "Streaming…" : "Refresh (regenerate)"}
          </button>

          {aiMeta?.stopReason === "max_tokens" && (
            <span className="text-[11px] text-amber-300 inline-flex items-center gap-1">
              <AlertTriangle size={11} />
              Output truncated at max tokens — refresh to retry
            </span>
          )}
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          {/* Error state */}
          {error && (
            <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 flex items-start gap-3">
              <AlertTriangle
                size={18}
                className="text-red-400 flex-shrink-0 mt-0.5"
              />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-200 mb-1">
                  {error.code === "TICKER_NOT_FOUND"
                    ? `Ticker "${ticker}" not in SEC filer database`
                    : error.code === "NO_REVENUE_DATA"
                    ? `Found "${ticker}" but no fundamentals available`
                    : error.code === "MISSING_CONFIG"
                    ? "Research backend not configured"
                    : "Research lookup failed"}
                </p>
                <p className="text-xs text-arc-muted leading-relaxed">
                  {error.message}
                </p>
                {error.code === "TICKER_NOT_FOUND" && (
                  <p className="text-xs text-arc-dim mt-2 leading-relaxed">
                    Try a different ticker. Only U.S.-listed common stocks
                    with SEC filings are supported (no foreign listings,
                    crypto, or invalid symbols).
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Loading state — visible until first snapshot arrives */}
          {!error && !snapshot && (
            <div className="py-10 flex flex-col items-center justify-center gap-3 text-arc-muted">
              <Loader className="animate-spin" size={22} />
              <p className="text-sm">
                {phase === "connecting"
                  ? "Connecting to research engine…"
                  : "Fetching SEC filings…"}
              </p>
            </div>
          )}

          {/* Fundamentals snapshot */}
          {snapshot?.fundamentals?.fy?.length > 0 && (
            <div className="mb-6">
              <p className="eyebrow mb-2">EDGAR fundamentals</p>
              <div className="overflow-x-auto rounded-lg border border-arc-line">
                <table className="w-full text-xs">
                  <thead className="bg-arc-surface/40">
                    <tr>
                      <th className="px-2.5 py-1.5 text-left font-mono uppercase tracking-wide text-[10px] text-arc-dim">
                        FY
                      </th>
                      <th className="px-2.5 py-1.5 text-right font-mono uppercase tracking-wide text-[10px] text-arc-dim">
                        Revenue
                      </th>
                      <th className="px-2.5 py-1.5 text-right font-mono uppercase tracking-wide text-[10px] text-arc-dim">
                        YoY
                      </th>
                      <th className="px-2.5 py-1.5 text-right font-mono uppercase tracking-wide text-[10px] text-arc-dim">
                        Gross M.
                      </th>
                      <th className="px-2.5 py-1.5 text-right font-mono uppercase tracking-wide text-[10px] text-arc-dim">
                        Op M.
                      </th>
                      <th className="px-2.5 py-1.5 text-right font-mono uppercase tracking-wide text-[10px] text-arc-dim">
                        Net M.
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {snapshot.fundamentals.fy.map((row) => (
                      <tr
                        key={row.fy}
                        className="border-t border-arc-line/40 text-arc-ink"
                      >
                        <td className="px-2.5 py-1.5 font-mono">{row.fy}</td>
                        <td className="px-2.5 py-1.5 text-right font-mono">
                          {formatBigDollars(row.revenue)}
                        </td>
                        <td className="px-2.5 py-1.5 text-right font-mono">
                          {formatPercent(row.revenueYoY)}
                        </td>
                        <td className="px-2.5 py-1.5 text-right font-mono">
                          {formatPercent(row.grossMargin)}
                        </td>
                        <td className="px-2.5 py-1.5 text-right font-mono">
                          {formatPercent(row.operatingMargin)}
                        </td>
                        <td className="px-2.5 py-1.5 text-right font-mono">
                          {formatPercent(row.netMargin)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* No-AI partial data view — ETFs, funds, foreign filers,
              recent IPOs. Shown INSTEAD of the AI section. */}
          {noAi && !error && (
            <div className="rounded-xl border border-arc-line bg-arc-surface/30 p-4 flex items-start gap-3">
              <Info
                size={18}
                className="text-arc-gold flex-shrink-0 mt-0.5"
              />
              <div className="flex-1 space-y-2">
                <p className="text-sm font-medium text-arc-ink">
                  No AI analysis for this entity
                </p>
                <p className="text-xs text-arc-muted leading-relaxed">
                  {noAi.reason}
                </p>
                {noAi.entityType && (
                  <p className="text-[10px] font-mono uppercase tracking-wide text-arc-dim">
                    SEC entity type: {noAi.entityType}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* AI analysis — hidden when noAi is set */}
          {!noAi && (snapshot || aiText) && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="eyebrow">AI Equity Analysis</p>
                {phase === "ai-streaming" && (
                  <span className="inline-flex items-center gap-1.5 text-[11px] text-arc-dim">
                    <Loader size={10} className="animate-spin" />
                    streaming
                  </span>
                )}
              </div>

              {/* Empty-state placeholder before any chunks arrive */}
              {!aiText && !error && phase !== "done" && (
                <div className="py-6 text-center text-arc-dim text-xs">
                  Claude is generating the grounded analysis…
                  <br />
                  Typically 60–90 seconds for fresh, &lt;1s for cached.
                </div>
              )}

              {/* Streaming markdown view */}
              {aiText && (
                <div className="prose-arc">
                  <MarkdownView source={aiText} />
                </div>
              )}

              {/* Footer with usage / model meta */}
              {aiMeta?.usage && phase === "done" && !error && (
                <div className="mt-6 pt-4 border-t border-arc-line/40 flex flex-wrap gap-x-4 gap-y-1 text-[10px] font-mono uppercase tracking-wide text-arc-dim">
                  <span>model: {aiMeta.model || "—"}</span>
                  <span>
                    tokens: {aiMeta.usage.input_tokens ?? "?"} in /{" "}
                    {aiMeta.usage.output_tokens ?? "?"} out
                  </span>
                  <span>stop: {aiMeta.stopReason || "?"}</span>
                </div>
              )}
            </div>
          )}

          {/* Disclaimer footer */}
          <div className="mt-6 pt-4 border-t border-arc-line">
            <p className="text-[10px] text-arc-dim leading-relaxed text-center">
              AI-generated research grounded in SEC EDGAR filings.
              Educational only — not investment advice, not personalized.
              ARCrypto is not a registered investment adviser.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // Portal to <body> so the modal isn't trapped under any parent
  // overflow: hidden / transform-style: preserve-3d / z-index gotchas.
  return createPortal(modal, document.body);
};

/* ─── Small utilities ─────────────────────────────────────────── */

const Chip = ({ children, gold, muted }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] ${
      gold
        ? "border-arc-gold/40 text-arc-gold bg-arc-gold/5"
        : muted
        ? "border-arc-line text-arc-dim bg-transparent"
        : "border-arc-line text-arc-muted bg-arc-surface/40"
    }`}
  >
    {children}
  </span>
);

function formatBigDollars(n) {
  if (n == null) return "—";
  const abs = Math.abs(n);
  if (abs >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  return `$${n.toLocaleString()}`;
}

function formatPercent(p) {
  if (p == null) return "—";
  return `${(p * 100).toFixed(1)}%`;
}

function timeAgo(iso) {
  if (!iso) return "";
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return "";
  const delta = Date.now() - then;
  const minutes = Math.floor(delta / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default StockResearchModal;
